import { all, takeLatest, call, select, put } from 'redux-saga/effects';
import organizationsApi from '../../api/organizations';
import accountsApi from '../../api/accounts';
import contactsApi from '../../api/contacts';
import pipelinesApi from '../../api/pipelines';
import customObjectsApi from '../../api/customObjects';
import constants, {
  LEVEL_ID_FIELD,
} from '../../common/constants/CommonConstants';
import onAntdTableExport from '../../common/util/onAntdTableExport';
import {
  exportData,
  exportEntityList,
} from '../routines/entityDataExportRoutines';

const {
  ORGANIZATION_LEVEL,
  ACCOUNT_LEVEL,
  CONTACT_LEVEL,
  PIPELINE_LEVEL,
  CUSTOM_OBJECT_LEVEL,
} = constants;

const mapLevelToApiFetchMethod = {
  [ORGANIZATION_LEVEL]: organizationsApi.getOrganizations,
  [ACCOUNT_LEVEL]: accountsApi.getAccounts,
  [CONTACT_LEVEL]: contactsApi.getContacts,
  [PIPELINE_LEVEL]: pipelinesApi.getPipelines,
  [CUSTOM_OBJECT_LEVEL]: customObjectsApi.getCustomObjectInstances,
};

export const makeSelectorForMyEntitiesFlag = (level) => {
  switch (level) {
    case ORGANIZATION_LEVEL:
      return (state) => state.organizations.myEntitiesOnly;
    case ACCOUNT_LEVEL:
      return (state) => state.accounts.myEntitiesOnly;
    case PIPELINE_LEVEL:
      return (state) => state.pipelines.myEntitiesOnly;
    default:
      throw new Error('Invalid level');
  }
};

function* fetchEntityList(rawLevel, sorting, filters, query, additionalParams) {
  const level = [
    ORGANIZATION_LEVEL,
    ACCOUNT_LEVEL,
    CONTACT_LEVEL,
    PIPELINE_LEVEL,
  ].includes(rawLevel)
    ? rawLevel
    : CUSTOM_OBJECT_LEVEL;
  const displayCurrency = yield select(
    (state) => state.currentUser.user.displayCurrency
  );

  const params = {
    page: 1,
    pageSize: Number.MAX_SAFE_INTEGER,
    ...sorting,
    filters,
    query,
    ...additionalParams,
  };

  if (level !== CONTACT_LEVEL && level !== CUSTOM_OBJECT_LEVEL) {
    const myEntitiesOnly = yield select(makeSelectorForMyEntitiesFlag(level));

    params.myEntitiesOnly = myEntitiesOnly;
  }

  const response = yield call(
    mapLevelToApiFetchMethod[level],
    displayCurrency,
    params,
    rawLevel
  );

  return response.data;
}

function* exportEntityListSaga(action) {
  const { payload = {} } = action;
  const { level, columns, sorting, filters, query, ...additionalParams } =
    payload;

  if (Array.isArray(columns) && columns.length > 0) {
    const entityList = yield call(
      fetchEntityList,
      level,
      sorting,
      filters,
      query,
      additionalParams
    );
    const idColumnLevel = [
      ORGANIZATION_LEVEL,
      ACCOUNT_LEVEL,
      CONTACT_LEVEL,
      PIPELINE_LEVEL,
    ].includes(level)
      ? level
      : CUSTOM_OBJECT_LEVEL;
    const idColumn = {
      exportTitle: 'Velaris ID',
      // due to issue in export method needs to be a func
      exportData: (row) => row[LEVEL_ID_FIELD[idColumnLevel]],
    };
    yield call(onAntdTableExport(entityList, [idColumn, ...columns]));
  }
  yield put(exportEntityList.success());
}

function* exportDataSaga({ payload }) {
  const { dataList, idColumn, columns } = payload;
  if (Array.isArray(columns) && columns.length > 0) {
    yield call(onAntdTableExport(dataList, [idColumn, ...columns]));
  }
  yield put(exportData.success());
}

export default function* root() {
  yield all([takeLatest(exportEntityList, exportEntityListSaga)]);
  yield all([takeLatest(exportData.TRIGGER, exportDataSaga)]);
}
